import { useCallback, useContext, useSyncExternalStore } from "react";
import de from "../messages/de.json";
import { LocaleContext } from "./i18n-context";
import {
  BCP47,
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  LOCALE_STORAGE_KEY,
  LOCALES,
  parseLocale,
  type Locale,
} from "./i18n-locale";

export { LOCALES, DEFAULT_LOCALE, LOCALE_COOKIE, parseLocale, BCP47 };
export type { Locale };

/**
 * Custom i18n runtime (ported from penteratracking; `.claude/rules/frontend.md`
 * forbids swapping in next-intl).
 *
 * **Only the active catalog is bundled (backlog C1).** `de.json` is imported
 * statically — it is the default locale, the authoring locale and the fallback
 * for every miss, so it is always needed. Every other catalog is an `await
 * import()` behind {@link ensureCatalog}, which the bundler emits as its own
 * async chunk. Before this split both catalogs sat in one shared chunk that 41
 * of 43 route manifests referenced: 354.051 B raw / 105.431 B gzip on every
 * page's critical path.
 *
 * **Catalogs are registered client-side only, on purpose.** Loading EN during
 * SSR would make the server's output depend on whether some *earlier* request
 * had already warmed the module cache — the same URL would render English or
 * German depending on process age, and hydration would mismatch whenever the
 * server won that race. Keeping the registry browser-only makes the first
 * render deterministic (German text, correct `lang`, correct locale-formatted
 * dates), followed by exactly one transition once the catalog chunk lands.
 */

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

type Messages = typeof de;

/** Loaded catalogs. `de` is always present; others arrive via {@link ensureCatalog}. */
const catalogs: Partial<Record<Locale, Messages>> = { de };

/** In-flight loads, so N concurrent callers share one chunk request. */
const pending = new Map<Locale, Promise<void>>();

let currentLocale: Locale = DEFAULT_LOCALE;
const listeners: Set<() => void> = new Set();

/**
 * `useSyncExternalStore` compares snapshots with `Object.is`, so the snapshot
 * cannot be the bare locale: switching to a locale whose catalog is still
 * loading would produce the *same* snapshot when the chunk lands, React would
 * bail out of re-rendering, and the UI would stay on the German fallback
 * forever. Bumping a revision on every notification makes "the catalog arrived"
 * an observable change.
 */
interface LocaleSnapshot {
  locale: Locale;
  revision: number;
}

let snapshot: LocaleSnapshot = { locale: DEFAULT_LOCALE, revision: 0 };

/**
 * Server snapshots are per-locale singletons: `getServerSnapshot` must return a
 * stable reference or React re-renders forever.
 */
const SERVER_SNAPSHOTS: Record<Locale, LocaleSnapshot> = {
  de: { locale: "de", revision: 0 },
  en: { locale: "en", revision: 0 },
};

function notify(): void {
  snapshot = { locale: currentLocale, revision: snapshot.revision + 1 };
  listeners.forEach((listener) => listener());
}

/** Client snapshot for `useSyncExternalStore` — see {@link LocaleSnapshot}. */
function getSnapshot(): LocaleSnapshot {
  return snapshot;
}

/**
 * Loads a locale's catalog chunk. Resolves immediately when it is already
 * present, and is a no-op on the server (see the module note above).
 */
export async function ensureCatalog(locale: Locale): Promise<void> {
  if (catalogs[locale] || typeof window === "undefined") return;
  const inFlight = pending.get(locale);
  if (inFlight) return inFlight;

  const load = (async () => {
    // Only `en` exists besides the statically-imported `de`; the switch keeps
    // the import specifiers literal so the bundler can emit one chunk per
    // locale instead of a glob over the whole messages directory.
    if (locale === "en") {
      const mod = await import("../messages/en.json");
      catalogs.en = mod.default as unknown as Messages;
    }
    notify();
  })().finally(() => {
    pending.delete(locale);
  });

  pending.set(locale, load);
  return load;
}

/** True once the locale's catalog is usable (always true for the default locale). */
export function isCatalogLoaded(locale: Locale): boolean {
  return catalogs[locale] !== undefined;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  // Not `__Host-`/Secure: it must be readable server-side over the dev HTTP
  // origin and is non-sensitive (a render preference). SameSite=Lax is enough.
  document.cookie = `${name}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

/**
 * Applies a locale on this device: instant re-render, `<html lang>`, the
 * localStorage fast path and the `ui-locale` cookie the root layout reads on the
 * next document load. Server persistence (the `ui.locale` user preference) is
 * the caller's concern — see `useLocalePreference`.
 *
 * Synchronous by design: `getLocale()`, `lang` and `aria-pressed` update in the
 * same tick. The catalog chunk is fetched in the background and triggers a
 * second notification when it arrives, so a switch to a not-yet-loaded locale
 * shows the German fallback for exactly one chunk fetch.
 */
export function setLocale(locale: Locale): void {
  currentLocale = locale;
  if (typeof window !== "undefined") {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    document.documentElement.lang = locale;
    writeCookie(LOCALE_COOKIE, locale);
  }
  notify();
  void ensureCatalog(locale);
}

export function getLocale(): Locale {
  return currentLocale;
}

export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Seeds the client store from the locale the SERVER rendered with (the
 * `ui-locale` cookie), exactly once, before anything else renders.
 *
 * The cookie is authoritative here even if `localStorage` disagrees: the SSR
 * markup was produced from it, so anything else would be a hydration mismatch.
 * A stale localStorage value is reconciled by `LocaleProvider` *after* mount —
 * one deliberate transition instead of a corrupted first render.
 *
 * Browser-only: on the server the store is process-global and shared by every
 * concurrent render, so it must never carry request state.
 */
let seeded = false;
export function seedLocale(locale: Locale): void {
  if (typeof window === "undefined" || seeded) return;
  seeded = true;
  currentLocale = locale;
  snapshot = { locale, revision: snapshot.revision };
  document.documentElement.lang = locale;
  writeCookie(LOCALE_COOKIE, locale);
  void ensureCatalog(locale);
}

/**
 * The device-local locale memory, or null when this device never chose one.
 * `LocaleProvider` uses it to reconcile a first visit that predates the cookie.
 */
export function readStoredLocale(): Locale | null {
  if (typeof window === "undefined") return null;
  return parseLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
}

/** Test seam: forget the one-shot seed so a suite can re-seed a fresh store. */
export function resetLocaleSeedForTests(): void {
  seeded = false;
}

type NestedKeyOf<T> = T extends object
  ? { [K in keyof T]: K extends string ? K | `${K}.${NestedKeyOf<T[K]>}` : never }[keyof T]
  : never;

export type TranslationKey = NestedKeyOf<Messages>;

/**
 * Every key that sits under `P.` in the catalog, with the prefix stripped —
 * the return type of {@link scoped}'s translator.
 */
export type TranslationKeySuffix<P extends string> = TranslationKey extends infer K
  ? K extends `${P}.${infer S}`
    ? S
    : never
  : never;

function lookup(locale: Locale, key: string): string | undefined {
  const keys = key.split(".");
  let value: unknown = catalogs[locale];
  for (const k of keys) {
    value = (value as Record<string, unknown>)?.[k];
  }
  return typeof value === "string" ? value : undefined;
}

/** Turn a dotted key into a readable last-resort label, e.g. "isms.myTasks" -> "My Tasks". */
function humanizeKey(key: string): string {
  const last = key.split(".").pop() ?? key;
  const spaced = last
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Resolve a key for a locale, falling back to the `de` catalog, then a humanized label. */
function resolve(locale: Locale, key: string): string {
  return lookup(locale, key) ?? lookup(DEFAULT_LOCALE, key) ?? humanizeKey(key);
}

/**
 * {@link resolve}, but taking the whole snapshot.
 *
 * It exists so the translator closure in {@link useTranslation} CAPTURES the
 * snapshot object rather than `snapshot.locale`. React's `useCallback` honours
 * the hand-written dependency array; the React Compiler (`reactCompiler: true`
 * in `next.config.ts`) does not — it derives dependencies from what the closure
 * actually reads, and narrowed `[snapshot]` to `[snapshot.locale]`. The
 * revision-only notification that fires when an async catalog chunk lands
 * therefore produced a NEW snapshot with the SAME locale, `t` kept its identity,
 * and every compiler-memoized subtree kept rendering the German fallback for the
 * rest of the session. Reading `snapshot` as a whole is what keeps the two
 * transforms in agreement. (Found by P1-14, once the test suite started running
 * the same transform production ships.)
 */
function resolveSnapshot(snapshot: LocaleSnapshot, key: string): string {
  return resolve(snapshot.locale, key);
}

export function t(key: TranslationKey): string {
  return resolve(currentLocale, key);
}

/**
 * Whether a dotted path resolves to a string leaf in the authoritative (German)
 * catalog.
 *
 * Exists so callers that build keys from *backend* identifiers — webhook event
 * names, feature flags — can degrade to the raw identifier instead of
 * `humanizeKey`'s guess, without importing a second copy of the catalog into
 * their chunk (that import is now an eslint error; this is its replacement).
 */
export function hasKey(key: string): boolean {
  return lookup(DEFAULT_LOCALE, key) !== undefined;
}

/**
 * Resolve a key in an EXPLICIT locale, independent of the active one.
 *
 * The command palette (§3.10) needs it: indexing every entry's label in BOTH
 * catalogs is what makes "tenant" find "Mandanten" without a hand-maintained
 * synonym table for every route — the EN catalog already *is* the synonym list.
 *
 * Self-healing by design (backlog C1): asking for a locale whose catalog is not
 * loaded starts the fetch and answers from German this once. Nothing therefore
 * has to pre-download the inactive catalog "just in case" — it arrives the first
 * time a user actually opens the palette, and the store notification re-renders
 * the index with the real cross-locale terms.
 */
export function translateIn(locale: Locale, key: TranslationKey): string {
  if (!catalogs[locale]) void ensureCatalog(locale);
  return resolve(locale, key);
}

/**
 * Prefix-bound translator that KEEPS type checking (backlog C5).
 *
 * The pattern it replaces — `const tb = (k: string) => t(\`prefix.${k}\` as
 * TranslationKey)` — silently accepts typos and renders `humanizeKey`'s guess,
 * and makes the dead-key audit blind to every key reached that way. `scoped`
 * has the identical call shape but its argument is the *set of suffixes that
 * actually exist* under the prefix, so a typo is a compile error.
 *
 * ```ts
 * const tb = scoped("isms.detail.bearbeitung", t);
 * tb("title");      // ok
 * tb("titel");      // ← compile error
 * ```
 */
export function scoped<P extends string>(
  prefix: P,
  translate: (key: TranslationKey) => string,
): Scoped<P> {
  return (key) => translate(`${prefix}.${key}` as TranslationKey);
}

/**
 * The type of a {@link scoped} translator — write it on props instead of
 * `(k: string) => string`, which throws the checking away again the moment the
 * translator is handed to a child component.
 */
export type Scoped<P extends string> = (key: TranslationKeySuffix<P>) => string;

/**
 * {@link scoped} for leaves that arrive from the BACKEND as `string` — status
 * codes, scope names, feature keys, relation kinds. The prefix is still checked
 * against the catalog; only the leaf cannot be, because no frontend type knows
 * it.
 *
 * This is the ONLY sanctioned escape hatch, and it is deliberately named: the
 * pattern it replaces (`t(\`prefix.${x}\` as never)`) disabled checking on the
 * prefix too, so a whole namespace could be renamed or deleted with nothing
 * failing. Reach for `scoped` unless the leaf is genuinely untyped server data.
 */
export function scopedDynamic<P extends string>(
  prefix: P,
  translate: (key: TranslationKey) => string,
): (key: string) => string {
  return (key) => translate(`${prefix}.${key}` as TranslationKey);
}

/* ------------------------------------------------------------------------- *
 * Locale-aware formatting (backlog C3)
 *
 * Before this, `lib/i18n.ts` exposed no formatter at all, so ~30 call sites
 * improvised: `lib/utils.ts` hardcoded "de-DE", eight files reimplemented with
 * "en-US", two with "en-GB", and eight passed no locale at all (browser
 * dependent → a hydration mismatch waiting to happen). One BCP-47 map
 * (`lib/i18n-locale.ts`) is now the only place a locale tag is written down.
 *
 * `locale` is an explicit parameter, not a module read: components must pass the
 * value from `useTranslation()` so the server render (which resolves the locale
 * from the cookie via context) and the client render agree.
 * ------------------------------------------------------------------------- */

function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
};

/** `31.12.2026` (de) / `12/31/2026` (en). Invalid/empty input renders `placeholder`. */
export function formatDate(
  value: Date | string | number | null | undefined,
  locale: Locale = currentLocale,
  placeholder = "-",
): string {
  const d = toDate(value);
  if (!d) return placeholder;
  return d.toLocaleDateString(BCP47[locale], DATE_OPTIONS);
}

/**
 * `31.12.` (de) / `12/31` (en) — the compact form for chart axes, where a
 * four-digit year on every tick is noise. Year-agnostic on purpose: callers that
 * span a year boundary label the axis ends with {@link formatDate} instead.
 */
export function formatDayMonth(
  value: Date | string | number | null | undefined,
  locale: Locale = currentLocale,
  placeholder = "-",
): string {
  const d = toDate(value);
  if (!d) return placeholder;
  return d.toLocaleDateString(BCP47[locale], { day: "2-digit", month: "2-digit" });
}

/** `31.12.2026, 14:05` (de) / `12/31/2026, 02:05 PM` (en). */
export function formatDateTime(
  value: Date | string | number | null | undefined,
  locale: Locale = currentLocale,
  placeholder = "-",
): string {
  const d = toDate(value);
  if (!d) return placeholder;
  return d.toLocaleString(BCP47[locale], { ...DATE_OPTIONS, ...TIME_OPTIONS });
}

/**
 * Relative time in the active locale: `vor 12 Minuten` (de) / `12 minutes ago` (en).
 *
 * This is the replacement for the deleted `formatRelativeTime` in `lib/utils.ts`,
 * built where that file's note says it belongs: on `Intl.RelativeTimeFormat`,
 * next to the other locale-aware helpers, with the locale as an explicit
 * parameter. The old one emitted hardcoded English into a German-default UI.
 *
 * Used by P1-2's telemetry surfaces, where the age of a number is as load-bearing
 * as the number: „34/50 Plätze“ is a different fact at two minutes and at six hours.
 * The largest unit that fits is chosen (seconds → minutes → hours → days), because
 * „vor 512 Minuten“ is technically true and operationally useless.
 */
export function formatRelativeTime(
  value: Date | string | number | null | undefined,
  locale: Locale = currentLocale,
  placeholder = "-",
  now: Date = new Date(),
): string {
  const d = toDate(value);
  if (!d) return placeholder;

  const seconds = Math.round((d.getTime() - now.getTime()) / 1000);
  const magnitude = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat(BCP47[locale], { numeric: "auto" });

  if (magnitude < 60) return formatter.format(seconds, "second");
  if (magnitude < 3600) return formatter.format(Math.round(seconds / 60), "minute");
  if (magnitude < 86_400) return formatter.format(Math.round(seconds / 3600), "hour");
  return formatter.format(Math.round(seconds / 86_400), "day");
}

/**
 * `1,2 MB` / `3,4 GB` — binary units, locale-formatted number, unit suffix in the
 * SI-prefixed form German operators read on a storage dashboard. Bytes below 1 KB
 * render as bytes; `null`/`undefined` renders `placeholder` (never `0 B`, which
 * would claim a tenant stores nothing when we simply did not measure).
 */
export function formatBytes(
  value: number | null | undefined,
  locale: Locale = currentLocale,
  placeholder = "-",
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return placeholder;

  const units = ["B", "KB", "MB", "GB", "TB", "PB"] as const;
  let size = Math.abs(value);
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }

  const digits = unit === 0 ? 0 : size < 10 ? 1 : 0;
  const rendered = (value < 0 ? -size : size).toLocaleString(BCP47[locale], {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${rendered} ${units[unit]}`;
}

/** `1.234,5` (de) / `1,234.5` (en). */
export function formatNumber(
  value: number | null | undefined,
  locale: Locale = currentLocale,
  options?: Intl.NumberFormatOptions,
  placeholder = "-",
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return placeholder;
  return value.toLocaleString(BCP47[locale], options);
}

/**
 * React hook for translations that properly handles locale changes.
 *
 * The server snapshot is the per-request locale published by `LocaleProvider`
 * from the `ui-locale` cookie — NOT the module store, which is process-global on
 * the server and would leak one user's locale into another's HTML. The client
 * snapshot is the live store, seeded from the same value before the first
 * render, so hydration matches by construction (backlog C4: this is what
 * removed `suppressHydrationWarning` from `<html lang>`).
 */
export function useTranslation() {
  const initialLocale = useContext(LocaleContext);
  // The whole snapshot is the dependency, not just `locale`: its identity also
  // changes when an async catalog chunk lands, which is what re-runs `t` for
  // memoized children after a switch to a not-yet-loaded locale.
  const snapshot = useSyncExternalStore(
    subscribeLocale,
    getSnapshot,
    () => SERVER_SNAPSHOTS[initialLocale],
  );

  // `resolveSnapshot(snapshot, …)`, not `resolve(snapshot.locale, …)`: the
  // closure must capture the snapshot OBJECT so both React's dependency array
  // and the React Compiler's inferred dependency are its identity. See
  // {@link resolveSnapshot}.
  const translate = useCallback(
    (key: TranslationKey): string => resolveSnapshot(snapshot, key),
    [snapshot],
  );

  return { t: translate, locale: snapshot.locale, setLocale };
}
